import { checkIfRolAdmin } from '../../../../common/check-role';
import { StorageManager } from '../../../../common/storage-manager.class';
import { environment } from '../../../../../environments/environment';
import { ClienteModel } from '../../../../models/cliente.model';
import { FincasModel } from '../../../../models/form-common/fincas.module';
import { ParcelasModel } from '../../../../models/form-common/parcelas.model';
import { SectorModel } from '../../../../models/form-common/sector.model';
import { MantenimientoModel } from '../../../../models/trabajadores/mantenimiento.model';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    FilteredValue,
    InputType,
    ReactiveDropdownCatalogs,
} from '@fitogest/reactive-form-builder/interfaces';
import { getDropdownSelectedSingle, list } from '@fitogest/reactive-form-builder/utils';
import {
    calculateDurationBetweenTimes,
    formatNativeTimeAsHoursMinutes,
    isNativeEndTimeBeforeStart,
    isValidNativeTime,
} from 'src/app/common/date-time-utils';

const TRUTHY_VALUES = new Set<unknown>([1, '1', true, 'true']);

export interface MtoHourGroup {
    readonly startField: string;
    readonly endField: string;
    readonly totalField: string;
}

export interface MtoFieldSpec<T extends object> {
    readonly name: string;
    readonly label: string;
    readonly type: InputType;
    readonly width?: number;
    readonly data?: DropdownData<unknown>;
    readonly defaultValue?: string | boolean | number | Date | T | null | undefined;
    readonly isEditable?: boolean;
    readonly isRequired?: boolean;
    readonly isVisible?: boolean;
    readonly isRememberedOnDuplicate?: boolean;
    readonly valuePrimaryKey?: string;
    readonly filter?: (it: FilteredValue<unknown>) => boolean | undefined;
    readonly photoField?: string;
    readonly hoursSource?: Omit<MtoHourGroup, 'totalField'>;
}

export interface MtoSectionSpec<T extends object> {
    readonly name: string;
    readonly width?: number;
    readonly isVisible?: boolean;
    readonly fields: MtoFieldSpec<T>[];
}

export interface MtoFormOptions<T extends object> {
    readonly name: string;
    readonly header: string;
    readonly database: string;
    readonly signatureFolder: string;
    readonly sections: MtoSectionSpec<T>[];
    readonly dropdownCatalogs?: ReactiveDropdownCatalogs;
}

export interface MtoMainFieldsOptions {
    readonly includeHours?: boolean;
    readonly includeMantenimiento?: boolean;
    readonly includePrioridad?: boolean;
    readonly hoursFieldName?: string;
    readonly horasLabel?: string;
}

export class MtoReactiveFormDefinition<T extends object> {
    public readonly env = {
        appName: environment.appName,
        serverUrl: environment.serverUrl,
        hasClients: environment.features.hasClients,
        userCanSeeClientes: environment.features.userCanSeeClientes,
        canSeeClientes: environment.features.hasClients && (
            environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin'
        ),
        clienteName: environment.features.clienteName,
        fincaName: environment.features.fincaName,
        fincasName: environment.features.fincasName,
        parcelasName: environment.features.parcelasName,
        sectorName: environment.features.sectorName,
        showTareasMto: environment.features.showTareasMto,
    };

    public readonly lists = {
        clientes: list<ClienteModel>(),
        fincas: list<FincasModel>(),
        sectores: list<SectorModel>(['...', null]),
        parcelas: list<ParcelasModel>(['...', null]),
        trabajadores: list<MantenimientoModel>(['...', null]),
        prioridades: list<string>(['...', null], 'Alta', 'Realizada'),
        portes: list<string>(['...', null], 'Propios', 'Proveedores'),
        diasSemana: list<string>(
            ['Lunes', '1'],
            ['Martes', '2'],
            ['Miércoles', '3'],
            ['Jueves', '4'],
            ['Viernes', '5'],
            ['Sábado', '6'],
            ['Domingo', '7'],
        ),
        metodosRiego: list<string>('Aspersor', 'Difusor', 'Goteo'),
        maquinariasAlquiler: list<string>(
            ['...', '0'],
            ['JOFEMESA', '1'],
            ['PRIMOTI', '2'],
            ['TOLLQUICK', '3'],
            ['MALCOP', '4'],
            ['ALQUICESMAR', '5'],
            ['OTROS', '6'],
        ),
    };

    public formType: FormType;
    public model: Partial<T>;
    public formFields!: Form<T>;
    public fieldNames: FormField<T>[] = [];
    public fieldLabels: string[] = [];

    private readonly user = StorageManager.getUser();
    private readonly client = StorageManager.getClient();
    private hourGroups: MtoHourGroup[] = [];

    constructor(type: FormType = FormType.CREATION, model: Partial<T> = {}) {
        this.formType = +type;
        this.model = model;
    }

    public calculateHoras(startField = 'hora_inicio', endField = 'hora_fin'): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.getModelValue(startField)),
            this.normalizeTimeValue(this.getModelValue(endField)),
        );
    }

    public getHourUpdatesForChangedField(changedFieldName: string): Record<string, string> {
        const updates: Record<string, string> = {};

        for (const group of this.hourGroups) {
            if (group.startField !== changedFieldName && group.endField !== changedFieldName) {
                continue;
            }

            updates[group.totalField] = this.calculateHoras(group.startField, group.endField);
        }

        return updates;
    }

    public updateMantenimientoLabel(): void {
        const field = this.getFieldByName('mantenimiento');

        if (!field) {
            return;
        }

        field.label = this.isTruthy(this.getModelValue('mantenimiento')) ? 'Creación' : 'Mantenimiento';
    }

    protected initializeMtoForm(options: MtoFormOptions<T>): void {
        this.hourGroups = [];
        this.fieldNames = [];
        this.fieldLabels = [];

        const sections = [
            ...options.sections,
            this.getHiddenSection(),
        ].map((section, sectionIndex) => this.toFormSection(section, sectionIndex + 1));

        this.formFields = {
            name: options.name,
            header: options.header,
            type: this.formType,
            sections,
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: true,
            ...(options.dropdownCatalogs ? { dropdownCatalogs: options.dropdownCatalogs } : {}),
            signatureConfiguration: {
                database: options.database,
                folder: options.signatureFolder,
                field: 'signature_img',
            },
        };
    }

    protected getDefaultMainFieldSpecs(options: MtoMainFieldsOptions = {}): MtoFieldSpec<T>[] {
        const includeHours = options.includeHours ?? true;
        const includeMantenimiento = options.includeMantenimiento ?? true;
        const includePrioridad = options.includePrioridad ?? true;
        const hoursFieldName = options.hoursFieldName ?? 'horas_totales';

        const fields: MtoFieldSpec<T>[] = [
            {
                name: 'id_cliente',
                label: this.env.clienteName,
                type: InputType.DROPDOWN_SEARCH,
                data: this.lists.clientes as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                width: 3,
                isRequired: true,
                isVisible: this.env.canSeeClientes && !this.client && this.env.hasClients,
            },
            {
                name: 'fecha',
                label: 'Fecha',
                type: InputType.CALENDAR,
                width: 3,
                isRequired: true,
                defaultValue: this.formType === FormType.CREATION ? new Date() : undefined,
            },
            {
                name: 'id_finca',
                label: this.env.fincaName,
                type: InputType.DROPDOWN_SEARCH,
                data: this.lists.fincas as DropdownData<unknown>,
                valuePrimaryKey: 'id',
                filter: (it): boolean => this.filterFincaByClient(it.value as FincasModel | null),
                width: 3,
                isRequired: true,
            },
        ];

        if (includeHours) {
            fields.push(
                {
                    name: 'hora_inicio',
                    label: 'Hora Inicio',
                    type: InputType.TIME,
                    width: 3,
                    isRequired: checkIfRolAdmin(),
                    isRememberedOnDuplicate: !this.env.showTareasMto,
                },
                {
                    name: 'hora_fin',
                    label: 'Hora Fin',
                    type: InputType.TIME,
                    width: 3,
                    isRequired: checkIfRolAdmin(),
                    defaultValue: this.getDefaultHoraFin(),
                    isRememberedOnDuplicate: !this.env.showTareasMto,
                },
                this.hourTotalField(hoursFieldName, options.horasLabel ?? 'Horas', 'hora_inicio', 'hora_fin', 3),
            );
        }

        if (includePrioridad) {
            fields.push({
                name: 'prioridad',
                label: 'Prioridad/Realizada',
                type: InputType.DROPDOWN,
                data: this.lists.prioridades as DropdownData<unknown>,
                defaultValue: this.env.showTareasMto && this.user.rol === 'user' ? 'Realizada' : undefined,
                width: 3,
            });
        }

        if (includeMantenimiento) {
            fields.push({
                name: 'mantenimiento',
                label: this.isTruthy(this.getModelValue('mantenimiento')) ? 'Creación' : 'Mantenimiento',
                type: InputType.SWITCH,
                width: 3,
            });
        }

        return fields;
    }

    protected field(
        name: string,
        label: string,
        type: InputType,
        options: Partial<MtoFieldSpec<T>> = {},
    ): MtoFieldSpec<T> {
        return {
            name,
            label,
            type,
            ...options,
        };
    }

    protected numericField(name: string, label: string, width = 3): MtoFieldSpec<T> {
        return this.field(name, label, InputType.NUMERIC, { width });
    }

    protected switchField(name: string, label: string, width = 3): MtoFieldSpec<T> {
        return this.field(name, label, InputType.SWITCH, { width });
    }

    protected hourField(name: string, label: string, width = 3): MtoFieldSpec<T> {
        return this.field(name, label, InputType.TIME, {
            width,
            isRequired: checkIfRolAdmin(),
            isRememberedOnDuplicate: !this.env.showTareasMto,
        });
    }

    protected hourTotalField(
        name: string,
        label: string,
        startField: string,
        endField: string,
        width = 3,
    ): MtoFieldSpec<T> {
        return this.field(name, label, InputType.ONLY_TEXT, {
            width,
            hoursSource: { startField, endField },
            isRememberedOnDuplicate: !this.env.showTareasMto,
        });
    }

    protected workerSection(label = 'Profesionales'): MtoSectionSpec<T> {
        return {
            name: '',
            fields: [
                {
                    name: 'ids_trabajadores',
                    label,
                    type: InputType.MULTISELECT,
                    data: this.lists.trabajadores as DropdownData<unknown>,
                    defaultValue: this.getDefaultWorkerId(),
                    valuePrimaryKey: 'id',
                    filter: (it): boolean => String((it.value as MantenimientoModel | null)?.activo ?? '') === '1',
                    width: 6,
                },
            ],
        };
    }

    protected workOrderSection(): MtoSectionSpec<T> {
        return {
            name: '',
            fields: [
                this.field('orden_trabajo', 'Orden de trabajo', InputType.TEXTAREA, {
                    width: 12,
                    isEditable: checkIfRolAdmin(),
                    photoField: 'imagen',
                }),
            ],
        };
    }

    protected observationSection(label = 'Observaciones'): MtoSectionSpec<T> {
        return {
            name: '',
            fields: [
                this.field('observaciones', label, InputType.TEXTAREA, {
                    width: 12,
                    photoField: 'image_after',
                }),
            ],
        };
    }

    protected isTruthy(value: unknown): boolean {
        return TRUTHY_VALUES.has(value);
    }

    private toFormSection(section: MtoSectionSpec<T>, order: number): FormSection<T> {
        const fields = section.fields
            .filter((field) => field.isVisible !== false)
            .map((field, index) => this.toFormField(field, index + 1));

        return {
            name: section.name,
            width: section.width ?? 12,
            isVisible: section.isVisible ?? true,
            fields,
            order,
        };
    }

    private toFormField(spec: MtoFieldSpec<T>, order: number): FormField<T> {
        if (spec.hoursSource) {
            this.hourGroups.push({
                startField: spec.hoursSource.startField,
                endField: spec.hoursSource.endField,
                totalField: spec.name,
            });
        }

        const input: FormField<T>['input'] = {
            type: spec.type,
            isEditable: spec.isEditable ?? true,
        };

        if (spec.defaultValue !== undefined) {
            input.defaultValue = spec.defaultValue;
        }

        if (spec.data !== undefined) {
            input.data = spec.data;
        }
        const dependencySources = spec.hoursSource
            ? [spec.hoursSource.startField, spec.hoursSource.endField]
            : this.getDefaultDependencySources(spec.name);
        const field: FormField<T> = {
            name: spec.name,

            label: spec.label,
            input,
            ...(dependencySources ? {
                dependencies: dependencySources.map((source) => ({
                    source,
                    target: spec.name,
                    kind: spec.hoursSource ? 'computed' : 'legacy',
                    legacy: !spec.hoursSource,
                })),
            } : {}),
            width: spec.width ?? 3,
            conditions: this.conditions({
                isRequired: spec.isRequired ?? false,
                isVisible: spec.isVisible ?? true,
                isRememberedOnDuplicate: spec.isRememberedOnDuplicate ?? true,
            }),
            order,
        };

        if (spec.type === InputType.CALENDAR) {
            field.input.calendarConfiguration = {
                calendarView: 'date',
                calendarDateFormat: 'dd/mm/yy',
            };
        }

        if (spec.type === InputType.TEXTAREA) {
            field.input.textAreaConfiguration = {
                hasVoiceTranscription: true,
                canTakePhoto: Boolean(spec.photoField),
                photoField: spec.photoField,
            };
        }

        if (spec.valuePrimaryKey !== undefined || spec.filter !== undefined) {
            const dropdownConfiguration: NonNullable<FormField<T>['input']['dropdownConfiguration']> = {};

            if (spec.valuePrimaryKey !== undefined) {
                dropdownConfiguration.valuePrimaryKey = spec.valuePrimaryKey;
            }

            if (spec.filter !== undefined) {
                dropdownConfiguration.filter = spec.filter as (it: FilteredValue<T>) => boolean | undefined;
            }

            field.input.dropdownConfiguration = dropdownConfiguration;
        }

        if (spec.hoursSource) {
            const source = spec.hoursSource;

            field.computed = {
                dependsOn: [source.startField, source.endField],
                resolve: (context) => this.formatHoursMinutes(
                    this.normalizeTimeValue(context.getValue(source.startField)),
                    this.normalizeTimeValue(context.getValue(source.endField)),
                ),
            };
            field.updateValue = () => this.calculateHoras(source.startField, source.endField);
        }

        if (field.conditions.isVisible && spec.type !== InputType.HIDDEN) {
            this.fieldNames.push(field);
            this.fieldLabels.push(field.label);
        }

        return field;
    }

    private getDefaultDependencySources(fieldName: string): string[] | undefined {
        if (fieldName === 'id_finca') {
            return ['id_cliente'];
        }

        if (fieldName === 'id_sector') {
            return ['id_finca'];
        }

        if (fieldName === 'ids_parcelas') {
            return ['id_finca', 'id_sector'];
        }

        return undefined;
    }

    private getHiddenSection(): MtoSectionSpec<T> {
        return {
            name: '',
            fields: [
                this.field('id', '', InputType.HIDDEN, {
                    defaultValue: this.getModelValue('id') as string | number | boolean | Date | T | null | undefined,
                    isEditable: false,
                    width: 2,
                }),
                this.field('id_cliente', '', InputType.HIDDEN, {
                    defaultValue: this.getInitialClientId(),
                    isEditable: false,
                    isVisible: this.env.hasClients && !this.env.canSeeClientes,
                    width: 2,
                }),
                this.field('id_usuario', '', InputType.HIDDEN, {
                    defaultValue: this.formType === FormType.EDITION
                        ? this.getModelValue('id_usuario') as string | undefined
                        : String(this.user.id ?? ''),
                    isEditable: false,
                    width: 2,
                }),
                this.field('imagen', '', InputType.HIDDEN, {
                    defaultValue: this.getModelValue('imagen') as string | undefined,
                    isEditable: false,
                    width: 2,
                }),
                this.field('image_after', '', InputType.HIDDEN, {
                    defaultValue: this.getModelValue('image_after') as string | undefined,
                    isEditable: false,
                    width: 2,
                }),
                this.field('signature_img', '', InputType.HIDDEN, {
                    defaultValue: this.getModelValue('signature_img') as string | undefined,
                    isEditable: false,
                    width: 2,
                }),
                this.field('id_ref_usuario', '', InputType.HIDDEN, {
                    defaultValue: String(this.user.id ?? ''),
                    isEditable: false,
                    isVisible: this.env.appName === 'donjardin' && this.formType !== FormType.CREATION,
                    width: 2,
                }),
            ],
        };
    }

    private getInitialClientId(): string | undefined {
        return this.client?.id ?? this.getSelectedClientId() ?? undefined;
    }

    private getSelectedClientId(): string | undefined {
        const finca = getDropdownSelectedSingle(this.lists.fincas) as FincasModel | null;

        return finca?.id_cliente?.toString() ?? this.client?.id ?? undefined;
    }

    private filterFincaByClient(finca: FincasModel | null): boolean {
        if (!finca) {
            return true;
        }

        if (!this.env.canSeeClientes || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedCliente = getDropdownSelectedSingle(this.lists.clientes);

        if (selectedCliente) {
            return String(finca.id_cliente ?? '') === String(selectedCliente.id ?? '');
        }

        if (this.client) {
            return String(finca.id_cliente ?? '') === String(this.client.id ?? '');
        }

        return true;
    }

    protected getDefaultHoraFin(): string | undefined {
        if (!this.env.showTareasMto || this.user.rol !== 'user' || this.formType === FormType.EDITION) {
            return undefined;
        }

        return formatNativeTimeAsHoursMinutes(new Date());
    }

    private getDefaultWorkerId(): string | undefined {
        if (this.user.rol !== 'user' || this.formType !== FormType.CREATION) {
            return undefined;
        }

        const userId = String(this.user.id ?? '').trim();

        return userId.length > 0 ? userId : undefined;
    }

    private getModelValue(fieldName: string): unknown {
        return (this.model as Record<string, unknown>)[fieldName];
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        if (!isValidNativeTime(startTime) || !isValidNativeTime(endTime)) {
            console.error('Invalid time format. Expected HH:mm.', {
                startTime,
                endTime,
            });

            return '';
        }

        if (isNativeEndTimeBeforeStart(startTime, endTime)) {
            console.warn('End time is before start time.', {
                startTime,
                endTime,
            });

            return '';
        }

        return calculateDurationBetweenTimes(startTime, endTime);
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return formatNativeTimeAsHoursMinutes(value);
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private getFieldByName(fieldName: string): FormField<T> | null {
        for (const section of this.formFields.sections ?? []) {
            const found = section.fields.find((field) => field.name === fieldName);

            if (found) {
                return found;
            }
        }

        return null;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: overrides.isRememberedOnDuplicate ?? true,
            isRequired: overrides.isRequired ?? false,
            isRetained: overrides.isRetained ?? false,
            hasUndoEnabled: overrides.hasUndoEnabled ?? false,
            isVisible: overrides.isVisible ?? true,
        };
    }
}
